import { Menu, Group, ActionIcon, Text, Tooltip } from "@mantine/core";
import { IconDots } from "@tabler/icons";
import { useTranslation } from "react-i18next";

const translateLabel = (t, label, data) => {
  if (typeof label === "function") return label(data);
  if (!label) return label;
  return t(label);
};

const renderActionButtons = (actions, data, onActionBtnClick, rowIndex, t) =>
  (actions || []).map((action, index) => (
    <Tooltip
      key={index}
      label={translateLabel(t, action.label, data)}
      position="top"
      withArrow
      openDelay={500}
    >
      <ActionIcon
        size="sm"
        variant="subtle"
        onClick={(e) => {
          e.stopPropagation();
          onActionBtnClick({ action: action.name, data, rowIndex });
        }}
      >
        <action.icon
          size={action.iconSize ?? 16}
          stroke={action.stroke ?? 1.5}
          {...action.props}
        />
      </ActionIcon>
    </Tooltip>
  ));

const translateTooltip = (t, tooltip, data) => {
  if (typeof tooltip === "function") return tooltip(data);
  if (!tooltip) return tooltip;
  return t(tooltip);
};

const renderMenuItem = ({
  action,
  index,
  data,
  rowIndex,
  onActionBtnClick,
  t,
}) => {
  const visible =
    typeof action.visible === "function"
      ? action.visible(data)
      : action.visible !== false;
  if (!visible) return null;

  const disabled =
    typeof action.props?.disabled === "function"
      ? action.props.disabled(data)
      : action.props?.disabled;
  const props = { ...action.props, disabled };
  const label = translateLabel(t, action.label, data);
  const disabledTooltip = disabled
    ? translateTooltip(t, action.disabledTooltip, data)
    : null;

  const disabledStyle = disabled
    ? { opacity: 0.5, cursor: "not-allowed" }
    : {};

  const menuItem = action.icon ? (
    <Menu.Item
      key={index}
      icon={<action.icon size={16} stroke={1.5} style={disabledStyle} />}
      {...props}
      style={disabledStyle}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) return;
        onActionBtnClick({ action: action.name, data, rowIndex });
      }}
    >
      <Text size="xs" color={disabled ? "dimmed" : undefined}>
        {label}
      </Text>
    </Menu.Item>
  ) : (
    <Menu.Item
      key={index}
      {...props}
      style={disabledStyle}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) return;
        onActionBtnClick({ action: action.name, data, rowIndex });
      }}
    >
      <Text size="xs" color={disabled ? "dimmed" : undefined}>
        {label}
      </Text>
    </Menu.Item>
  );

  if (!disabledTooltip) return menuItem;

  return (
    <Tooltip
      key={index}
      label={disabledTooltip}
      multiline
      maw={320}
      withArrow
      withinPortal
    >
      <div>{menuItem}</div>
    </Tooltip>
  );
};

const renderActionMenu = (actionMenu, data, onActionBtnClick, rowIndex, t) => {
  if (!actionMenu || actionMenu.length === 0) return null;

  return (
    <Menu transition="pop" withArrow position="bottom-end">
      <Menu.Target>
        <ActionIcon onClick={(e) => e.stopPropagation()}>
          <IconDots size={16} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown py="xs">
        {actionMenu.map((action, index) =>
          renderMenuItem({
            action,
            index,
            data,
            rowIndex,
            onActionBtnClick,
            t,
          })
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

const ActionCell = ({ column, rowIndex, data, onActionBtnClick }) => {
  const { t } = useTranslation();

  return (
    <Group spacing={4} noWrap position="right" align="center">
      {renderActionButtons(column.actions, data, onActionBtnClick, rowIndex, t)}
      {renderActionMenu(column.actionMenu, data, onActionBtnClick, rowIndex, t)}
    </Group>
  );
};

const ActionCellRender = (column, rowIndex, data, onActionBtnClick) => (
  <ActionCell
    column={column}
    rowIndex={rowIndex}
    data={data}
    onActionBtnClick={onActionBtnClick}
  />
);

export default ActionCellRender;
